import type {
  FieldValues,
  Path,
  PathValue,
  UseFormReturn,
} from "react-hook-form";

import { useState } from "react";

import { toast } from "@/components/toast/toast-queue.ts";
import apiClient from "@/lib/api/client.ts";

import type { Asset } from "../types.ts";

export function useDeleteAllAssets<TFormValues extends FieldValues>(
  field: Path<TFormValues>,
  assets: Asset[],
  setError: (error: null | string) => void,
  methods: UseFormReturn<TFormValues>,
): {
  handleDeleteAll: () => Promise<void>;
  isDeletingAll: boolean;
} {
  const [isDeletingAll, setIsDeletingAll] = useState(false);

  const handleDeleteAll = async () => {
    setError(null);
    setIsDeletingAll(true);

    const attachmentIds = assets
      .map((asset) => asset.attachmentId)
      .filter((id): id is number => typeof id === "number");

    try {
      await Promise.all(
        attachmentIds.map((attachmentId) =>
          apiClient
            .delete(`/attachments/${encodeURIComponent(attachmentId)}`)
            .res(),
        ),
      );

      methods.setValue(field, [] as PathValue<TFormValues, Path<TFormValues>>);

      toast.add({
        description: "All files have been deleted successfully.",
        timeout: 2000,
        title: "Files deleted",
        type: "success",
      });
    } catch (error) {
      console.error("Error deleting all assets:", error);
      setError("Failed to delete all files. Please try again.");
    } finally {
      setIsDeletingAll(false);
    }
  };

  return { handleDeleteAll, isDeletingAll };
}
